import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";
import Pagination from "../components/Pagination";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiSettings,
  FiClipboard,
  FiSearch,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";

const EMPTY_FORM = {
  parid: "",
  par_value: "",
};

export default function ManageSetting() {
  // Data
  const [data, setData] = useState([]);
  const [params, setParams] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search
  const [search, setSearch] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 10;
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Form
  const [form, setForm] = useState({
    ...EMPTY_FORM,
  });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Modals
  const [formModal, setFormModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // Fetch settings
  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await API.get("/settings", {
        params: {
          page,
          limit,
          search,
        },
      });

      const responseData = response.data;

      if (responseData && !Array.isArray(responseData)) {
        const rows = Array.isArray(responseData.data)
          ? responseData.data
          : [];

        const totalCount = Number(responseData.total || 0);

        setData(rows);
        setTotal(totalCount);
        setTotalPages(
          Number(
            responseData.totalPages ||
              Math.ceil(totalCount / limit)
          )
        );
      } else {
        const rows = Array.isArray(responseData) ? responseData : [];

        setData(rows);
        setTotal(rows.length);
        setTotalPages(Math.ceil(rows.length / limit));
      }
    } catch (error) {
      console.error("Fetch settings error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load settings"
      );

      setData([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  // Load settings
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch parameters
  useEffect(() => {
    const fetchParameters = async () => {
      try {
        const response = await API.get("/settings/parameters");

        setParams(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Fetch parameters error:", error);

        toasts.error(
          error.response?.data?.error ||
            error.response?.data?.message ||
            "Failed to load parameters"
        );
      }
    };

    fetchParameters();
  }, []);

  // Search
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Reset form
  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);
  };

  // Open add modal
  const openAddModal = () => {
    resetForm();
    setFormModal(true);
  };

  // Close form modal
  const closeFormModal = () => {
    if (saving) {
      return;
    }

    setFormModal(false);
    resetForm();
  };

  // Edit
  const startEdit = (row) => {
    setForm({
      parid: row.parid || "",
      par_value: row.par_value || "",
    });

    setEditId(row.setId);
    setViewModal(null);
    setFormModal(true);
  };

  // View
  const handleView = (row) => {
    setViewModal(row);
  };

  // Form change
  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // Submit
  const handleSubmit = async () => {
    if (!form.parid) {
      toasts.error("Please select parameter");
      return;
    }

    if (!form.par_value.trim()) {
      toasts.error("Parameter value is required");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        parid: form.parid,
        par_value: form.par_value.trim(),
      };

      if (editId) {
        await API.put(`/settings/${editId}`, payload);
        toasts.success("Setting updated successfully");
      } else {
        await API.post("/settings", payload);
        toasts.success("Setting added successfully");
      }

      setFormModal(false);
      resetForm();

      await fetchData();
    } catch (error) {
      console.error("Submit setting error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(`/settings/${delModal.setId}`);

      toasts.success("Setting deleted successfully");

      setDelModal(null);

      if (data.length === 1 && page > 1) {
        setPage((previous) => previous - 1);
      } else {
        await fetchData();
      }
    } catch (error) {
      console.error("Delete setting error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };
 // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };

 // Return
  return (
    <Layout activePage="Settings">
      <main className="dashboard-main">
        <div className="table-card">
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />
              <span>Manage Settings</span>
              <span className="rec-count ms-1">
                {total} settings
              </span>
            </h3>

            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch />
              </span>

              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={handleSearch}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            <button
              type="button"
              className="um-btn-primary"
              onClick={openAddModal}
            >
              <span className="um-add-icon">＋</span>
              Add Setting
            </button>
          </div>

          <div className="table-responsive">
            <table className="um-table">
              <thead>
                <tr className="um-table-head">
                  <th className="um-th text-center">#</th>
                  <th className="um-th text-center">Parameter</th>
                  <th className="um-th text-center">Value</th>
                  <th className="um-th text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`loading-${index}`}>
                      {Array.from({ length: 4 }).map(
                        (_, cellIndex) => (
                          <td
                            key={`skeleton-${cellIndex}`}
                            className="um-td text-center"
                          >
                            <div className="skeleton" />
                          </td>
                        )
                      )}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="empty-table-cell text-center py-5"
                    >
                      <div className="empty-icon">
                        <FiSettings />
                      </div>

                      <div className="empty-title">
                        No settings found
                      </div>

                      <div className="empty-sub">
                        Use the Add Setting button to add one
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => (
                    <tr
                      key={row.setId}
                      className={`um-row ${
                        index % 2 === 0 ? "even-row" : "odd-row"
                      }`}
                    >
                      <td className="um-td text-center">
                        {(page - 1) * limit + index + 1}
                      </td>

                      <td className="um-td text-center">
                        <span className="badge badge-success">
                          {row.parameter || "—"}
                        </span>
                      </td>

                      <td className="um-td text-center">
                        <div
                          style={{
                            maxWidth: "450px",
                            margin: "0 auto",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={row.par_value || ""}
                        >
                          {row.par_value || "—"}
                        </div>
                      </td>

                      <td className="um-td text-center">
                        <div className="action-buttons ">
                          <button
                            type="button"
                            className="action-btn action-view"
                            title="View"
                            onClick={() => handleView(row)}
                          >
                            <FiEye size={15} />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-edit"
                            title="Edit"
                            onClick={() => startEdit(row)}
                          >
                            <FiEdit size={15} />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-delete delete-enabled"
                            title="Delete"
                            onClick={() => setDelModal(row)}
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={10}
              loading={loading}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          </div>
        </div>

        {formModal && (
          <div className="um-overlay" onClick={closeFormModal}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <div className="form-card-icon">
                    {editId ? (
                      <FiEdit size={24} />
                    ) : (
                      <FiPlus size={24} />
                    )}
                  </div>

                  <div>
                    <h2 className="um-modal-title mb-0">
                      {editId ? "Edit Setting" : "Add Setting"}
                    </h2>

                    <p className="form-card-sub mb-0">
                      Manage setting information
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  ✕
                </button>
              </div>

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="field-label">
                      Parameter *
                    </label>

                    <select
                      className="rd-input"
                      value={form.parid}
                      onChange={(e) =>
                        handleChange("parid", e.target.value)
                      }
                    >
                      <option value="">
                        Select Parameter
                      </option>

                      {params.map((parameter) => (
                        <option
                          key={parameter.parid}
                          value={parameter.parid}
                        >
                          {parameter.parameter}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-12">
                    <label className="field-label">Value *</label>

                    <input
                      type="text"
                      className="rd-input"
                      style={{
                        textAlign: "start",
                      }}
                      placeholder="Enter setting value"
                      value={form.par_value}
                      onChange={(e) =>
                        handleChange(
                          "par_value",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner" />
                  ) : editId ? (
                    <>
                      <FiSave />
                      Update Setting
                    </>
                  ) : (
                    <>
                      <FiPlus />
                      Add Setting
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {viewModal && (
          <div
            className="um-overlay"
            onClick={() => setViewModal(null)}
          >
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="um-modal-head">
                <div>
                  <h2 className="um-modal-title mb-0">
                    <FiEye /> Setting Details
                  </h2>

                  <p className="form-card-sub mb-0">
                    View setting information
                  </p>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() => setViewModal(null)}
                >
                  ✕
                </button>
              </div>

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="small text-muted text-uppercase fw-semibold mb-1">
                      Parameter
                    </div>

                    <div className="bg-light rounded-3 border p-3">
                      <div className="fw-bold">
                        {viewModal.parameter || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-8">
                    <div className="small text-muted text-uppercase fw-semibold mb-2">
                      Value
                    </div>

                    <div className="bg-light rounded-3 border p-3">
                      <div
                        className="fw-semibold"
                        style={{
                          whiteSpace: "pre-wrap",
                          lineHeight: "1.7",
                          wordBreak: "break-word",
                        }}
                      >
                        {viewModal.par_value ||
                          "No value available."}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() => setViewModal(null)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const selected = viewModal;

                    setViewModal(null);

                    setTimeout(() => {
                      startEdit(selected);
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit Setting
                </button>
              </div>
            </div>
          </div>
        )}

        {delModal && (
          <div
            className="um-overlay"
            onClick={() => setDelModal(null)}
          >
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                <div className="delete-icon">
                  <FiTrash2 />
                </div>

                <h2 className="delete-title">
                  Delete Setting?
                </h2>

                <div className="delete-badges">
                  <span className="badge badge-success">
                    {delModal.parameter || "Setting"}
                  </span>
                </div>

                <p className="delete-description">
                  This setting will be permanently deleted.
                  <br />
                  <strong>
                    This action cannot be undone.
                  </strong>
                </p>

                <div className="delete-actions">
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() => setDelModal(null)}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 size={16} />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

    
      </main>
    </Layout>
  );
}
